import React from 'react';

import { UseFormReturn } from 'react-hook-form';

import { RoleEnum, UserCreateDTO, UserListDTO } from '_lib/api/apiTypes.shim';
import { Card } from '_lib/components/Card/Card';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { CheckboxFormItem } from '_lib/components/CheckboxFormItem/CheckboxFormItem';
import { Divider } from '_lib/components/Divider/Divider';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import { Stack } from '_lib/components/Stack/Stack';
import { SwitchFormItem } from '_lib/components/SwitchFormItem/SwitchFormItem';
import { TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';

import { AuthUser } from '../auth/auth.types';
import {
  ManageAccountUsers,
  ManageAppointmentWindows,
  ManageCallCenterUsers,
  ManageCompanies,
  ManageCompanyBilling,
  ManageCompanySettings,
  ManageCompanyWindows
} from '../auth/permissionHelpers';

export type UserFormValues = Pick<
  UserCreateDTO,
  'email' | 'firstName' | 'lastName' | 'role' | 'settings' | 'isSupervisor' | 'supervisorId' | 'teamId'
>;

const teamIdDisplayValues = ['None', 'Leads', 'Inbound', 'Outbound', 'Sales', 'Support', 'Other', 'USA Plus'] as const;

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 *
 * See https://github.com/orgs/react-hook-form/discussions/8311#discussioncomment-9759698
 */
type UserFormProps<T extends UserFormValues> = {
  userFormContext: T extends UserFormValues ? UseFormReturn<T> : never;
  authUser: AuthUser;
  isEditingOwnProfile?: boolean;
  isInactive?: boolean;
  supervisors?: UserListDTO[];
};

export const UserForm = <T extends UserFormValues>(props: UserFormProps<T>) => {
  const { userFormContext, authUser, isEditingOwnProfile, isInactive, supervisors } = props;

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.only('xs'));

  const { control, setValue, watch } = userFormContext;

  const userRole = watch('role');

  /** User Plus can view the details of an admin, but they cannot edit anything */
  const isUserViewingAdmin = authUser.role === RoleEnum.USER && userRole === RoleEnum.ADMIN;

  /** Readonly if user permission or if user is not active */
  const isReadOnly = isUserViewingAdmin || isInactive;

  /**
   * Role is disabled IF:
   * - auth users role === USER
   * - auth user is editing/viewing their profile
   * - The user being edited is a SYSTEM
   */
  const isRoleDisabled = authUser.role === RoleEnum.USER || isEditingOwnProfile || userRole === RoleEnum.SYSTEM;

  /**
   * - Cannot edit permissions if component is in readonly mode
   * - Cannot edit permissions when on your own profile
   * - Can only edit permissions for a USER. SYSTEM and ADMIN user permission settings are immutable.
   */
  const isPermissionsManagementDisabled = isReadOnly || !!isEditingOwnProfile || userRole !== RoleEnum.USER;

  const setAllPortalSettingsToValue = (value: boolean) => {
    if (isPermissionsManagementDisabled) return;
    if (ManageAppointmentWindows({ user: authUser })) setValue('settings.portal.appointmentWindows', value);
    if (ManageCompanyWindows({ user: authUser })) setValue('settings.portal.companyWindows', value);
    if (ManageAccountUsers({ user: authUser })) setValue('settings.portal.users', value);
  };

  const setAllCallPilotSettingsToValue = (value: boolean) => {
    if (isPermissionsManagementDisabled) return;
    if (ManageCompanies({ user: authUser })) setValue('settings.callPilot.companies', value);
    if (ManageCompanyBilling({ user: authUser })) setValue('settings.callPilot.companiesBilling', value);
    if (ManageCompanySettings({ user: authUser })) setValue('settings.callPilot.companiesSettings', value);
    if (ManageCallCenterUsers({ user: authUser })) setValue('settings.callPilot.users', value);
  };

  const availableRoles =
    userRole === RoleEnum.SYSTEM ? [RoleEnum.USER, RoleEnum.ADMIN, RoleEnum.SYSTEM] : [RoleEnum.USER, RoleEnum.ADMIN];

  return (
    <Stack width='100%' useFlexGap spacing={2}>
      <Stack direction={singleColumn ? 'column' : 'row'} spacing={2}>
        <TextFieldFormItem name='firstName' label='First Name' control={control} required disabled={isReadOnly} />
        <TextFieldFormItem name='lastName' label='Last Name' control={control} required disabled={isReadOnly} />
      </Stack>
      <Stack direction={singleColumn ? 'column' : 'row'} spacing={2}>
        <TextFieldFormItem name='email' label='Email Address' control={control} required disabled={isReadOnly} />
        <SelectFormItem
          name='role'
          label='Role'
          control={control}
          required
          disabled={isRoleDisabled || isReadOnly}
          onChange={(e) => {
            const newRoleIsNotUser = e.target.value !== RoleEnum.USER;
            setAllCallPilotSettingsToValue(newRoleIsNotUser);
            setAllPortalSettingsToValue(newRoleIsNotUser);
          }}>
          {availableRoles.map((role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </SelectFormItem>
      </Stack>
      {!isEditingOwnProfile && userRole !== RoleEnum.SYSTEM && (
        <Card>
          <PrimaryCardHeader title='Reporting' />
          <Stack sx={{ padding: 2, width: '100%' }} direction={'column'} spacing={2}>
            <Stack direction={singleColumn ? 'column' : 'row'} spacing={2} sx={{ width: '100%' }}>
              <SelectFormItem
                name='supervisorId'
                label='Supervisor'
                control={control}
                disabled={isEditingOwnProfile || isReadOnly}
                fullWidth={singleColumn}
                sx={{ width: singleColumn ? 'auto' : '50%' }}
                onChange={(e) => {
                  if (e.target.value === 'null') {
                    setValue('supervisorId', null);
                  }
                }}
                slotProps={{
                  select: {
                    renderValue: (value) => {
                      const supervisor = supervisors?.find((sup) => sup.id === value);
                      if (!supervisor) return 'No Supervisor';
                      return <>{`${supervisor.firstName} ${supervisor.lastName}`}</>;
                    },
                    value: watch('supervisorId') ?? 'null'
                  }
                }}>
                {supervisors &&
                  supervisors.length > 0 &&
                  supervisors.map((supervisor) => (
                    <MenuItem key={supervisor.id} value={supervisor.id}>
                      {`${supervisor.firstName} ${supervisor.lastName}`}
                    </MenuItem>
                  ))}
                <MenuItem value={'null'}>No Supervisor</MenuItem>
              </SelectFormItem>
              <CheckboxFormItem
                disabled={isEditingOwnProfile || isReadOnly}
                name='isSupervisor'
                label='Is Supervisor'
                control={control}
              />
            </Stack>
            <Stack direction={singleColumn ? 'column' : 'row'} sx={{ width: singleColumn ? '100%' : '50%' }}>
              <SelectFormItem name='teamId' label='Team' control={control} disabled={isEditingOwnProfile || isReadOnly}>
                {teamIdDisplayValues.map((value, index) => (
                  <MenuItem key={index} value={index}>
                    {value}
                  </MenuItem>
                ))}
              </SelectFormItem>
            </Stack>
          </Stack>
        </Card>
      )}
      {authUser.settings.callPilot.users && (
        <Card>
          <PrimaryCardHeader title='Permissions' />
          <Stack useFlexGap spacing={0}>
            <SwitchFormItem
              disabled={isPermissionsManagementDisabled || isReadOnly || !ManageCompanies({ user: authUser })}
              name='settings.callPilot.companies'
              label='Manage accounts and companies'
              control={control}
              sx={{ margin: 0, padding: 1 }}
            />
            <Divider />
            <SwitchFormItem
              disabled={isPermissionsManagementDisabled || isReadOnly || !ManageCompanyBilling({ user: authUser })}
              name='settings.callPilot.companiesBilling'
              label={`Manage a company's billing info`}
              control={control}
              sx={{ margin: 0, padding: 1 }}
            />
            <Divider />
            <SwitchFormItem
              disabled={isPermissionsManagementDisabled || isReadOnly || !ManageCompanySettings({ user: authUser })}
              name='settings.callPilot.companiesSettings'
              label={`Manage a company's settings`}
              control={control}
              sx={{ margin: 0, padding: 1 }}
            />
            <Divider />
            <SwitchFormItem
              disabled={isPermissionsManagementDisabled || isReadOnly || !ManageCallCenterUsers({ user: authUser })}
              name='settings.callPilot.users'
              label='Manage call center users'
              control={control}
              sx={{ margin: 0, padding: 1 }}
            />
          </Stack>
          <Stack useFlexGap spacing={0}>
            <Divider />
            <SwitchFormItem
              disabled={isPermissionsManagementDisabled || isReadOnly || !ManageAppointmentWindows({ user: authUser })}
              name='settings.portal.appointmentWindows'
              label='Manage schedule'
              control={control}
              sx={{ margin: 0, padding: 1 }}
            />
            <Divider />
            <SwitchFormItem
              disabled={isPermissionsManagementDisabled || isReadOnly || !ManageCompanyWindows({ user: authUser })}
              name='settings.portal.companyWindows'
              label='Manage window types'
              control={control}
              sx={{ margin: 0, padding: 1 }}
            />
            <Divider />
            <SwitchFormItem
              disabled={isPermissionsManagementDisabled || isReadOnly || !ManageAccountUsers({ user: authUser })}
              name='settings.portal.users'
              label='Manage account users'
              control={control}
              sx={{ margin: 0, padding: 1 }}
            />
          </Stack>
        </Card>
      )}
    </Stack>
  );
};
